import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Grid, CircularProgress, Button, Paper } from "@mui/material";
import DeceasedDetail from "./DeceasedDetail";
import AssignedStaffDetails from "./AssignedStaffDetails";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../../store/Index";
import apiService from "../../../../../service/Api";
import { IStreamDetails } from "../../../../../types/streams";
import { toast } from "react-toastify";
import LinkIcon from "@mui/icons-material/Link";
import { User } from "../../../../users-admin/types/User";
import { NoAssignees } from "./StreamTableRow";

import PackageIcon from "../../../../../assets/icons/gift.svg";
import UserIcon from "../../../../../assets/icons/user-square.svg";
import Calendar from "../../../../../assets/icons/calendar-2.svg";
import DynamicThumbnailCard from "../../thumbnail/ThumbnailCard";


const BookingAndDeceasedDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [isLoadingAssignees, setIsLoadingAssignees] = useState<boolean>(false);
  const [errorLoadingAssignees, setErrorLoadingAssignees] =
    useState<boolean>(false);
  const [assignees, setAssignees] = useState<User[]>([]);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [streamDetails, setStreamDetails] = useState<IStreamDetails | null>(
    null
  );
  const [slug, setSlug] = useState<string | null>("");
  const [thumbnailRefreshTrigger, setThumbnailRefreshTrigger] = useState(0);

  useEffect(() => {
    fetchBookingDetails();
  }, []);

  useEffect(() => {
    if (!streamDetails) return;

    const deceased = streamDetails.booking?.deceased?.[0];

    const nameSource = deceased
      ? `${deceased.first_name} ${deceased.surname}`
      : streamDetails.title; // ✅ use stream title for non-funeral

    if (!nameSource) return;

    const slug = nameSource
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

    setSlug(slug);
  }, [streamDetails]);


  const fetchBookingDetails = async () => {
    setError(false);
    setIsLoading(true);
    try {
      const response = await apiService.get<IStreamDetails>(
        `/api/streams/stream/${id}/`
      );

      setStreamDetails(response.data);
    } catch (error) {
      toast.error("Failed to load stream. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        theme: "light",
      });
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMemoryBookAssignees = async () => {
    setIsLoadingAssignees(true);
    setErrorLoadingAssignees(false);

    try {
      const response = await apiService.get<any>(
        `/api/streams/stream/${id}/memory-book-assignees/`
      );

      setAssignees(response.data.data.assignees);
    } catch (error) {
      setErrorLoadingAssignees(true);
    } finally {
      setIsLoadingAssignees(false);
    }
  };

  return (
    <Box
      component={"section"}
      sx={{
        backgroundColor: reduxTheme.primary_colors[900],
        p: 1,
        borderRadius: 1,
      }}
    >
      <Box
        sx={{
          "& .MuiTypography-root": {
            fontSize: "16px",
            fontWeight: "600",
          },
        }}
      >
        {streamDetails && (
          <>
            <DeceasedDetail
              hasLink={false}
              detailHeader=""
              detailValue={
                streamDetails.booking?.deceased?.[0]
                  ? `${streamDetails.booking.deceased[0].first_name} ${streamDetails.booking.deceased[0].surname}`
                  : streamDetails.title || "N/A" // ✅ stream title fallback
              }
              url=""
            />
            <Box
              sx={{
                "& .MuiTypography-root": {
                  fontSize: "14px",
                  fontWeight: "400",
                },
              }}
            >
              <DeceasedDetail
                hasLink={false}
                detailHeader=""
                detailValue={
                  streamDetails.booking.deceased.length === 0
                    ? "N/A"
                    : `${new Date(
                        streamDetails.booking.deceased[0].date_of_birth
                      ).getFullYear()}-${new Date(
                        streamDetails.booking.deceased[0].date_of_death
                      ).getFullYear()}`
                }
                url=""
              />
            </Box>
          </>
        )}
      </Box>
      {isLoading && (
        <Box display={"flex"} justifyContent={"center"} pt={2} pb={2}>
          <CircularProgress size={20} />
        </Box>
      )}

      {error && !isLoading && !errorLoadingAssignees && (
        <Box pt={1} pb={1}>
          <Typography variant="body1" align="center">
            Something went wrong in collecting details
          </Typography>
          <Box display={"flex"} justifyContent={"center"} mt={1}>
            <Button
              onClick={fetchBookingDetails}
              variant="contained"
              sx={{ ml: "auto", mr: "auto" }}
            >
              Retry
            </Button>
          </Box>
        </Box>
      )}

      {!error && !isLoading && streamDetails && (
        <Grid container gap={5} justifyContent="flex-start">
          <Grid container direction="column" gap={0.1}>
            {/* Each row: header + value */}
            <Box
              display="grid"
              gridTemplateColumns="150px 1fr" // fixed header width, value takes remaining space
              alignItems="center"
              gap={1}
            >
              <DeceasedDetail
                hasLink={false}
                detailHeader="Stream Date"
                headerIcon={
                  <img
                    src={Calendar}
                    alt="User Icon"
                    style={{ width: 18, height: 18 }}
                  />
                }
                detailValue=""
                url=""
              />
              <DeceasedDetail
                hasLink={false}
                detailHeader=""
                detailValue={new Date(streamDetails.date).toLocaleDateString(
                  "en-GB",
                  {
                    day: "2-digit",
                    month: "long",
                    year: "numeric",
                  }
                )}
                url=""
              />
            </Box>

            <Box
              display="grid"
              gridTemplateColumns="150px 1fr"
              alignItems="center"
              gap={1}
            >
              <DeceasedDetail
                hasLink={false}
                detailHeader="Package"
                headerIcon={
                  <img
                    src={PackageIcon}
                    alt="User Icon"
                    style={{ width: 18, height: 18 }}
                  />
                }
                detailValue=""
                url=""
              />
              <DeceasedDetail
                hasLink={false}
                detailHeader=""
                detailValue={
                  streamDetails.booking.package_item
                    ? streamDetails.booking.package_item.name
                    : "N/A"
                }
                url=""
              />
            </Box>

            <Box
              display="grid"
              gridTemplateColumns="150px 1fr"
              alignItems="center"
              gap={1}
            >
              <DeceasedDetail
                hasLink={false}
                detailHeader="Contact Person"
                headerIcon={
                  <img
                    src={UserIcon}
                    alt="User Icon"
                    style={{ width: 18, height: 18 }}
                  />
                }
                detailValue=""
                url=""
              />
              <DeceasedDetail
                hasLink={false}
                detailHeader=""
                detailValue={
                  streamDetails.booking.contact
                    ? `${streamDetails.booking.contact.first_name} ${streamDetails.booking.contact.last_name}`
                    : "N/A"
                }
                url=""
              />
            </Box>
          </Grid>

          <Grid container sx={{ ml: 5}} direction="column" gap={1}>
            {slug && (
              <Button
                sx={{ backgroundColor: "info.main", borderRadius: "8px" }}
                variant="contained"
                href={`/memorial-page/${streamDetails.booking.id}/${slug}/`}
              >
                Memorial Page
              </Button>
            )}

            <Button
              sx={{ backgroundColor: "#AF52DE", borderRadius: "8px" }}
              variant="contained"
              href={`/bookings/${streamDetails.booking.id}`}
            >
              Booking Details
            </Button>

            <Button
              sx={{ borderRadius: "8px" }}
              variant="contained"
              href={`/stream-management/view/${streamDetails.id}`}
            >
              Stream Details
            </Button>
          </Grid>

          <Grid sx={{mt:-8,ml:5}}>
            <DynamicThumbnailCard
              refreshTrigger={thumbnailRefreshTrigger}
              streamData={streamDetails}
              streamId={streamDetails.id}
            />
          </Grid>

          {/* <Grid sx={{ ml: 4 }}>
            <Paper
              sx={{
                p: 2,
                mt:2.5,
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                backgroundColor: reduxTheme.primary_colors[800],
                cursor: "pointer",
                height:"90px"
              }}
            >
              <Typography sx={{ textAlign: "center",fontSize:"20px" }}>+</Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "medium",
                  textTransform: "capitalize",
                  color: reduxTheme.secondary_colors[200],
                 
                }}
              >
                assign crew
              </Typography>
              {/*   {isLoadingAssignees && <LoadingAssignees size={30} />}
              {!errorLoadingAssignees &&
                !isLoadingAssignees &&
                assignees.length === 0 && <NoAssignees size={30} />}

              {!errorLoadingAssignees &&
                !isLoadingAssignees &&
                assignees.length > 0 &&
                assignees.map((assignee) => {
                  return (
                    <AssignedStaffDetails
                      username={`${assignee.first_name} ${assignee.last_name}`}
                    />
                  );
                })} 
            </Paper>
          </Grid>
          */}
        </Grid>
      )}
    </Box>
  );
};

export default BookingAndDeceasedDetails;
